import moment from 'moment-timezone';
import UserSettingsRepository from '@/services/repository/UserSettingsRepository';
import 'moment/locale/sk';
import 'moment/locale/en-gb';
import 'moment/locale/de';
import i18n from '@/lang/lang';
import { UserSettingsKeys } from '@/entities/enums/UserSettingKeys';
import UserPreferences from './UserPreferences';
var userSettingsRepository;
/**
 * Clock format enum
 */
export var ClockFormat;
(function (ClockFormat) {
    ClockFormat["12h"] = " hh:mm A";
    ClockFormat["24h"] = " HH:mm";
})(ClockFormat || (ClockFormat = {}));
/**
 * Clock format enum
 */
export var ClockFormatWithSeconds;
(function (ClockFormatWithSeconds) {
    ClockFormatWithSeconds["12h"] = " hh:mm:ss A";
    ClockFormatWithSeconds["24h"] = " HH:mm:ss";
})(ClockFormatWithSeconds || (ClockFormatWithSeconds = {}));
export var DatetimeUnits;
(function (DatetimeUnits) {
    DatetimeUnits["SECONDS"] = "s";
    DatetimeUnits["MINUTES"] = "m";
    DatetimeUnits["HOURS"] = "h";
    DatetimeUnits["DAYS"] = "d";
    DatetimeUnits["MONTHS"] = "M";
    DatetimeUnits["YEARS"] = "y";
})(DatetimeUnits || (DatetimeUnits = {}));
const DATE_FORMATS = [
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'DD.MM.YYYY',
    'MM.DD.YYYY',
    'YYYY. MM. DD.',
    'DD-MM-YYYY',
    'MM-DD-YYYY'
];
const DEFAULT_TIMEZONE = moment.tz.guess();
const DEFAULT_CLOCK_FORMAT = '24h';
const DEFAULT_LOCALE = 'sk';
/**
 * Date time manager
 */
export default class DateTimeManager {
    _settings;
    constructor() {
        userSettingsRepository = UserSettingsRepository.getInstance();
    }
    /**
     * Asynchronous static function to create new instance of DateTimeManager
     * @param [useDefaultSettings] - choose if settings should be loaded or default settings should be loaded
     * @returns manager
     */
    static async CreateManager(useDefaultSettings = false) {
        let manager = new this();
        if (useDefaultSettings == false) {
            await manager.loadSettings();
        }
        else {
            manager.setMomentLocale(DEFAULT_LOCALE);
            const selectedDateFormat = UserPreferences.getPreference(UserPreferences.LocalStorageKeys.DateFormat);
            const selectedClockFormat = UserPreferences.getPreference(UserPreferences.LocalStorageKeys.TimeFormat);
            const selectedTimeZone = UserPreferences.getPreference(UserPreferences.LocalStorageKeys.Timezone);
            if (selectedDateFormat && selectedClockFormat && selectedTimeZone) {
                manager.setLocalPreferences(selectedTimeZone, selectedDateFormat, selectedClockFormat, 'false');
            }
            else {
                manager.setMomentTimezone(DEFAULT_TIMEZONE);
            }
        }
        return manager;
    }
    static minutesToDuration(minutes) {
        if (!minutes || minutes == -1)
            return i18n.t('generals.na').toString();
        var days = Math.floor(minutes / 1440);
        var hours = Math.floor(minutes / 60) % 24;
        var minutes = minutes % 60;
        return ((days > 0 ? i18n.tc('time.day', days) + ' ' : '') +
            (hours > 0 ? i18n.tc('time.hour', hours) + ' ' : '') +
            (minutes > 0 ? i18n.tc('time.minute', minutes) : ''));
    }
    areSameDate(date1, date2) {
        let usedTimezone = this.getSelectedTimezone();
        const momD1 = moment(date1).tz(usedTimezone);
        const momD2 = moment(date2).tz(usedTimezone);
        return momD1.date() === momD2.date();
    }
    /**
     * Formats a duration using an anchor point for accurate month/year calculation.
     * @param duration - The duration in milliseconds.
     * @param endTimestamp - Anchor in milliseconds representing the end of the duration.
     * @param maxUnits - Maximum number of time units to include in the output (0 for all).
     * @returns Localized formatted string (e.g., "1mo 3d 4h").
     */
    static formatDurationWithAnchor(duration, endTimestamp, maxUnits = 0) {
        if (duration == null || duration < 0 || !endTimestamp) {
            return i18n.t('generals.na').toString();
        }
        const end = moment(endTimestamp);
        const start = moment(endTimestamp - duration);
        // Units in descending order
        const units = [
            { unit: 'years', label: 'duration.year' },
            { unit: 'months', label: 'duration.month' },
            { unit: 'days', label: 'duration.day' },
            { unit: 'hours', label: 'duration.hour' },
            { unit: 'minutes', label: 'duration.minute' },
            { unit: 'seconds', label: 'duration.second' }
        ];
        const parts = [];
        // Progressively step forward from start to end date
        for (const { unit, label } of units) {
            const value = end.diff(start, unit);
            if (value > 0) {
                parts.push({ value, label });
                // Shifts the anchor forward for the next smaller unit
                start.add(value, unit);
            }
        }
        // Handle max units cap
        if (maxUnits > 0 && maxUnits < parts.length) {
            parts.splice(maxUnits);
        }
        if (parts.length === 0) {
            return `0${i18n.t('duration.second').toString()}`;
        }
        return parts.map((p) => `${p.value}${i18n.t(p.label).toString()}`).join(' ');
    }
    static timestampToDuration(timestamp) {
        if (timestamp == -1 || timestamp == null)
            return i18n.t('generals.na').toString();
        var days = Math.floor(timestamp / 86400);
        var hours = Math.floor(timestamp / 3600) % 24;
        var minutes = Math.floor(timestamp / 60) % 60;
        var seconds = timestamp % 60;
        return ((days <= 0 ? '' : days + 'd ') +
            (hours <= 0 && days <= 0 ? '' : hours + 'h ') +
            (minutes <= 0 && hours <= 0 && days <= 0 ? '0 min' : minutes + 'min '));
    }
    formatTimeSpan(time) {
        if (!time)
            return i18n.t('generals.na').toString();
        const [hours, minutes] = time.split(':').map(Number);
        const parts = [];
        if (hours > 0)
            parts.push(`${hours}${i18n.t('duration.hour')}`);
        if (minutes > 0)
            parts.push(`${minutes}${i18n.t('duration.minute')}`);
        return parts.length > 0 ? parts.join(' ') : `0${i18n.t('duration.minute')}`;
    }
    /**
     * Gets all month names
     * @returns all month names
     */
    getMonthNames() {
        return moment.months();
    }
    /**
     * Gets all timezones from moment.js
     * @returns Array of timezones names
     */
    getAllTimezones() {
        return moment.tz.names();
    }
    /**
     * Filter UTC and Europe timezones from moment.js
     * @returns Array of timezones names
     */
    getLimitedTimezones() {
        return moment.tz.names().filter((name) => name.toLowerCase().includes('europe/') || name.toLowerCase() === 'utc');
    }
    /**
     * Gets all supported date formats
     * @returns Array of all supported date formats
     */
    getAllDateFormats() {
        return DATE_FORMATS;
    }
    /**
     * Gets all clock formats identifiers
     * @returns Array of clock formats [12h,24h]
     */
    getAllClockFormats() {
        return Object.keys(ClockFormat);
    }
    /**
     * Gets selected timezone
     * @returns selected timezone
     */
    getSelectedTimezone() {
        return ((this._settings || {}).datetime || {}).timezone || DEFAULT_TIMEZONE;
    }
    /**
     * Gets selected date format
     * @returns selected date format
     */
    getSelectedDateFormat() {
        return ((this._settings || {}).datetime || {}).dateFormat || DATE_FORMATS[2];
    }
    /**
     * Gets selected clock format
     * @returns selected clock format
     */
    getSelectedClockFormat() {
        return ((this._settings || {}).datetime || {}).clockFormat || DEFAULT_CLOCK_FORMAT;
    }
    /**
     * Gets show TZ preference
     * @returns selected clock format
     */
    getTzInClockFormatPreference() {
        return this._settings?.datetime?.tzInFormat == 'true';
    }
    /**
     * Gets date format object for chart date formatting
     * @returns ChartDateFormat object with user date preferences
     */
    getChartDateFormat() {
        const currentLocale = moment.locale();
        switch (currentLocale) {
            case 'sk':
                return this.createChartDateFormatForSK();
                break;
            case 'en':
                return this.createChartDateFormatForEN();
                break;
            default:
                return this.createChartDateFormatForEN();
                break;
        }
    }
    /**
     * Minutes to formatted hours
     * @param minutes
     * @returns
     */
    minutesToUTCFormattedHours(minutes) {
        const format = this.getSelectedClockFormat();
        return moment.utc().startOf('day').add(minutes, 'minutes').format(this.getValueForClockFormat(format));
    }
    isSummerTime() {
        return moment().tz(this.getSelectedTimezone()).isDST();
    }
    tzUsesSummerTime() {
        return (moment([2010, 12, 20]).tz(this.getSelectedTimezone()).isDST() !=
            moment([2010, 5, 20]).tz(this.getSelectedTimezone()).isDST());
    }
    createChartDateFormatForSK() {
        let clockFormat = this.getSelectedClockFormat();
        let hourFormat = clockFormat == '12h' ? 'hh' : 'HH';
        let signForClockFormat = clockFormat == '12h' ? 'A' : '';
        let formats = {
            second: `${hourFormat}:mm:ss ${signForClockFormat}`,
            minute: `${hourFormat}:mm ${signForClockFormat}`,
            hour: `${hourFormat}:mm ${signForClockFormat}`,
            day: 'DD. MMM (ddd)',
            date: 'DD. MMM (ddd)',
            month: 'MMMM',
            year: 'YYYY'
        };
        return formats;
    }
    createChartDateFormatForEN() {
        let clockFormat = this.getSelectedClockFormat();
        let hourFormat = clockFormat == '12h' ? 'hh' : 'HH';
        let signForClockFormat = clockFormat == '12h' ? 'A' : '';
        let formats = {
            second: `${hourFormat}:mm:ss ${signForClockFormat}`,
            minute: `${hourFormat}:mm ${signForClockFormat}`,
            hour: `${hourFormat}:mm ${signForClockFormat}`,
            day: 'MMM DD (ddd)',
            date: 'MMM DD (ddd)',
            month: 'MMMM',
            year: 'YYYY'
        };
        return formats;
    }
    /**
     * Store selected timezone to user settings on server
     * @param timezone selected timezone name
     */
    async setSelectedTimezone(timezone) {
        this.setMomentTimezone(timezone);
        await this.saveSettings([UserSettingsKeys.DATETIME, UserSettingsKeys.TIMEZONE], timezone);
        await this.setSelectedFormat();
    }
    /**
     * Store selected date format to user settings on server
     * @param dateFormat selected date format
     */
    async setSelectedDateFormat(dateFormat) {
        await this.saveSettings([UserSettingsKeys.DATETIME, UserSettingsKeys.DATEFORMAT], dateFormat);
        await this.setSelectedFormat();
    }
    /**
     * Store selected clock format to user settings on server
     * @param clockFormat selected clock format
     */
    async setSelectedClockFormat(clockFormat) {
        await this.saveSettings([UserSettingsKeys.DATETIME, UserSettingsKeys.CLOCKFORMAT], clockFormat);
        await this.setSelectedFormat();
    }
    /**
     * Store selected clock format to user settings on server
     * @param clockFormat selected clock format
     */
    async setTzInClockFormatPreference(tzInFormat) {
        await this.saveSettings([UserSettingsKeys.DATETIME, UserSettingsKeys.TZ_IN_FORMAT], tzInFormat);
        await this.setSelectedFormat();
    }
    /**
     * Sets local preferences, used on public dashboard
     * @param timezone
     * @param dateFormat
     * @param clockFormat
     * @param tzInFormat
     */
    setLocalPreferences(timezone, dateFormat, clockFormat, tzInFormat) {
        this._settings = {
            datetime: {
                timezone: timezone,
                dateFormat: dateFormat,
                clockFormat: clockFormat,
                tzInFormat: tzInFormat,
                format: ''
            }
        };
        this.setMomentTimezone(this._settings.datetime.timezone);
    }
    formatTimeInInput(date) {
        let usedFormat = this.getFormat(null, '');
        return moment(date).format(usedFormat);
    }
    formatTimeRangeInInput(date) {
        let usedFormat = this.getFormat(null, '');
        let formatted = [null, null];
        if (date.length > 0)
            formatted[0] = moment(date[0]).format(usedFormat);
        if (date.length > 1)
            formatted[1] = moment(date[1]).format(usedFormat);
        return formatted.join(' - ');
    }
    formatDateInInput(date) {
        let usedFormat = this.getFormat(null, '');
        return moment(date).format(usedFormat);
    }
    formatDateTimeInInput(date) {
        let usedFormat = this.getFormat(null, null);
        return moment(date).format(usedFormat);
    }
    getMonthFilter(date) {
        let filters = {
            start: null,
            stop: null,
            startTS: null,
            stopTS: null
        };
        let startDate = moment(date);
        filters.startTS = startDate.unix();
        //asi start stop uz vobec netreba, iba TS
        filters.start = startDate.tz('UTC', false).toISOString();
        let endDate = moment(date).endOf('month');
        filters.stopTS = endDate.unix();
        filters.stop = endDate.tz('UTC', true).toISOString();
        return filters;
    }
    getDayFilter(date) {
        let filters = {
            start: null,
            stop: null,
            startTS: null,
            stopTS: null
        };
        let startDate = moment(date).tz('UTC', false).startOf('day');
        filters.startTS = startDate.unix();
        filters.start = startDate.toISOString();
        let endDate = startDate.clone().endOf('day');
        filters.stopTS = endDate.unix();
        filters.stop = endDate.toISOString();
        return filters;
    }
    getCustomFilter(dateFrom, dateTo, shiftFrom, unitFrom, shifTo, unitTo) {
        let filters = {
            start: null,
            stop: null,
            startTS: null,
            stopTS: null
        };
        let startDate = moment(dateFrom).tz('UTC', false).add(shiftFrom, unitFrom);
        filters.startTS = startDate.unix();
        filters.start = startDate.toISOString();
        let endDate = moment(dateTo).tz('UTC', false).add(shifTo, unitTo);
        filters.stopTS = endDate.unix();
        filters.stop = endDate.toISOString();
        return filters;
    }
    formatDateTimeForReports(date) {
        let usedTimezone = this.getSelectedTimezone();
        let momentDate = moment(date).tz(usedTimezone, true);
        return { ts: momentDate.unix(), text: momentDate.tz('UTC', false).toISOString() };
    }
    formatStartDateForReports(date) {
        let usedTimezone = this.getSelectedTimezone();
        let startDate = moment(date).tz(usedTimezone, true);
        return { ts: startDate.unix(), text: startDate.tz('UTC', false).toISOString() };
    }
    formatEndDateForReports(date, setEndOfDay = false) {
        let usedTimezone = this.getSelectedTimezone();
        let endDate = moment(date).tz(usedTimezone, true);
        if (setEndOfDay === true) {
            endDate = endDate.endOf('day');
        }
        return { ts: endDate.unix(), text: endDate.tz('UTC', false).toISOString() };
    }
    formatDateRange(dateArr) {
        let usedTimezone = this.getSelectedTimezone();
        let startDate = moment.tz(dateArr[0], usedTimezone);
        let endDate = moment.tz(dateArr[1], usedTimezone);
        return [startDate.toDate(), endDate.toDate()];
    }
    formatDate(date) {
        let usedDateFormat = this.getSelectedDateFormat();
        return moment(date).format(usedDateFormat);
    }
    formatDateOnlyFromTimestamp(ts) {
        if (!ts)
            return '-';
        let usedDateFormat = this.getSelectedDateFormat();
        return moment.unix(ts).utc().format(usedDateFormat);
    }
    toUtcDateOnly(ts) {
        const m = moment.unix(ts).utc();
        return new Date(Date.UTC(m.year(), m.month(), m.date()));
    }
    /**
     * Formats month name in input
     * @param date given date object from input field
     * @returns formatted date string (eg. January 2020)
     */
    formatMonthNameInInput(date) {
        return moment(date.getMonth() + 1, 'M').format('MMMM') + ' ' + moment(date).year();
    }
    getTimeStringForInputDate(date) {
        let usedTimezone = this.getSelectedTimezone();
        let usedFormat = this.getFormat(null, null);
        let localDate = moment.tz(moment(date).format(usedFormat), usedFormat, usedTimezone).toDate();
        return localDate.toISOString();
    }
    /**
     * Formats given date (default value for params: value from user settings)
     * @param date date value
     * @param [timezone] timezone name (default: value from user settings)
     * @param [format] date format
     * @param [clock] clock format
     * @returns Formatted date string
     */
    formatTime(date, timezone, format, clock, showTz) {
        if (!date || date.getFullYear() === 1970 || date.getFullYear() === 1 || !isFinite(date))
            return '-';
        let usedFormat = this.getFormat(format, clock, showTz);
        let usedTimezone = timezone || this.getSelectedTimezone();
        return moment(date).tz(usedTimezone).format(usedFormat);
    }
    getTimeDifference(date) {
        let differenceInSeconds = Math.floor(new Date().getTime() / 1000 - date.getTime() / 1000);
        var hours = Math.floor(differenceInSeconds / 3600) % 24;
        var minutes = Math.floor(differenceInSeconds / 60) % 60;
        var seconds = differenceInSeconds % 60;
        let timeUnit = { time: null, unit: null };
        if (hours > 0) {
            timeUnit.time = hours;
            if (hours == 1) {
                timeUnit.unit = 'hour';
            }
            else {
                timeUnit.unit = 'hours';
            }
        }
        else if (minutes > 0) {
            timeUnit.time = minutes;
            if (minutes == 1) {
                timeUnit.unit = 'minute';
            }
            else {
                timeUnit.unit = 'minutes';
            }
        }
        else {
            timeUnit.time = seconds;
            timeUnit.unit = 'seconds';
        }
        return timeUnit;
    }
    getTimeDifferenceBySegment(startDate, endDate) {
        let end = endDate || new Date();
        let differenceInSeconds = Math.floor(end.getTime() / 1000 - startDate.getTime() / 1000);
        var days = Math.floor(differenceInSeconds / 86400);
        var hours = Math.floor(differenceInSeconds / 3600) % 24;
        var minutes = Math.floor(differenceInSeconds / 60) % 60;
        let timeSegments = {
            days: null,
            hours: null,
            minutes: null,
            seconds: null
        };
        if (days > 0) {
            timeSegments.days = days;
        }
        if (hours > 0) {
            timeSegments.hours = hours;
        }
        if (minutes > 0) {
            timeSegments.minutes = minutes;
        }
        return timeSegments;
    }
    formatTimeOnly(date, withSeconds = false) {
        let usedFormat = this.getValueForClockFormat(this.getSelectedClockFormat(), withSeconds);
        let usedTimezone = this.getSelectedTimezone();
        return moment(date).tz(usedTimezone).format(usedFormat);
    }
    /**
     * Returns used timezone offset in minutes
     * @returns timezone offset in minutes
     */
    getUsedTzOffsetInMinutes() {
        let usedTimezone = this.getSelectedTimezone();
        return moment().tz(usedTimezone).utcOffset();
    }
    async saveSettings(keyPath, value) {
        this._settings = await userSettingsRepository.saveUserSettings([keyPath], [value]);
        this.setMomentLocale(this._settings.language);
        this.setMomentTimezone(this._settings.datetime.timezone);
    }
    /**
     * Loads user settings
     */
    async loadSettings() {
        this._settings = await userSettingsRepository.loadUserSettings();
        this.setMomentLocale(this._settings.language);
        this.setMomentTimezone(this._settings.datetime.timezone);
    }
    /**
     * Creates format string
     * @param [format] date format
     * @param [clock] clock format
     * @returns format string
     */
    getFormat(format, clock, showTz) {
        let tzPrefix = (showTz !== false && this.getTzInClockFormatPreference() == true) || showTz === true ? ' zz' : '';
        return ((format != '' ? format || this.getSelectedDateFormat() : '') +
            (clock != '' ? this.getValueForClockFormat(clock || this.getSelectedClockFormat()) + tzPrefix : ''));
    }
    /**
     * Sets selected format for formatter
     */
    async setSelectedFormat() {
        this._settings = await userSettingsRepository.saveUserSettings([[UserSettingsKeys.DATETIME, UserSettingsKeys.FORMAT]], [this.getFormat()]);
    }
    /**
     * Gets value for clock format
     * @param clockFormat clock format identifier
     * @returns value for clock format
     */
    getValueForClockFormat(clockFormat, withSeconds = false) {
        switch (clockFormat) {
            case '12h':
            case '24h':
                if (withSeconds)
                    return ClockFormatWithSeconds[clockFormat];
                return ClockFormat[clockFormat];
                break;
            default:
                throw 'Unknown clock format: ' + clockFormat;
        }
    }
    parseTimeOnly(time) {
        if (!time)
            return null;
        let usedTimezone = this.getSelectedTimezone();
        let m = moment.tz(time, ['HH:mm:ss', 'HH:mm'], true, usedTimezone);
        if (!m.isValid())
            return null;
        return m.toDate();
    }
    serializeTimeOnly(date) {
        return date ? moment(date).format('HH:mm') : null;
    }
    /**
     * Sets locale for moment.js based on app locale
     * @param appLocale: locale from user settings
     */
    setMomentLocale(appLocale) {
        moment.locale(appLocale);
    }
    setMomentTimezone(timezone) {
        //moment.tz.setDefault(timezone);
    }
}
